import { Duration } from 'effect';

export type ListingCacheOptions = {
  /** Refreshes cached activations after this duration. Zero refreshes every activation. */
  readonly staleTime?: Duration.Input;
  /** Limits retained product, facet, and append snapshot entries together. */
  readonly capacity?: number;
  /** Expires a retention lease after this duration from acquisition. */
  readonly retentionTime?: Duration.Input;
  /** Removes expired leases at this interval. */
  readonly cleanupInterval?: Duration.Input;
  readonly prefetchDelay?: Duration.Input;
  readonly prefetchConcurrency?: number;
  readonly prefetchEnabled?: boolean;
};

/** Keeps session retention unlimited until a tenant selects finite limits. */
export const listingCacheDefaults = {
  staleTime: Duration.zero,
  capacity: Number.POSITIVE_INFINITY,
  retentionTime: Duration.infinity,
  cleanupInterval: Duration.seconds(1),
  prefetchDelay: Duration.millis(150),
  prefetchConcurrency: 2,
  prefetchEnabled: true,
};

export const resolveListingCacheOptions = (options: ListingCacheOptions = {}) => {
  const capacity = options.capacity ?? listingCacheDefaults.capacity;
  const prefetchConcurrency =
    options.prefetchConcurrency ?? listingCacheDefaults.prefetchConcurrency;
  if (capacity !== Number.POSITIVE_INFINITY && (!Number.isInteger(capacity) || capacity < 1)) {
    throw new Error('The listing cache capacity must be a positive integer or Infinity.');
  }
  if (!Number.isInteger(prefetchConcurrency) || prefetchConcurrency < 1) {
    throw new Error('The listing prefetch concurrency must be a positive integer.');
  }
  const settings = {
    staleTime: Duration.fromInputUnsafe(options.staleTime ?? listingCacheDefaults.staleTime),
    capacity,
    retentionTime: Duration.fromInputUnsafe(
      options.retentionTime ?? listingCacheDefaults.retentionTime
    ),
    cleanupInterval: Duration.fromInputUnsafe(
      options.cleanupInterval ?? listingCacheDefaults.cleanupInterval
    ),
    prefetchDelay: Duration.fromInputUnsafe(
      options.prefetchDelay ?? listingCacheDefaults.prefetchDelay
    ),
    prefetchConcurrency,
    prefetchEnabled: options.prefetchEnabled ?? listingCacheDefaults.prefetchEnabled,
  };
  if (
    !Duration.isFinite(settings.cleanupInterval) ||
    Duration.toMillis(settings.cleanupInterval) <= 0
  ) {
    throw new Error('The listing cache cleanup interval must be positive and finite.');
  }
  return settings;
};
